import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Eyebrow, Reveal } from "./utils";

const faqs = [
  {
    q: "Who is Apex Flow for?",
    a: "Service businesses that already generate leads, calls, quotes, and customers but suspect revenue is slipping through missed follow-up, disorganized data, or weak reactivation. If you want more predictable growth from what you already have, Apex Flow is built for you.",
  },
  {
    q: "Do I need to already have a CRM?",
    a: "No. Many clients start without one or with a messy, underused setup. CRM setup, cleanup, and customer organization are part of getting started—we help install and organize your pipeline and customer data.",
  },
  {
    q: "Do you only run ads?",
    a: "No. Ads are just one possible tactic. We first identify the strongest revenue opportunity inside your business and only recommend paid advertising when it genuinely fits your goals, capacity, and customer base.",
  },
  {
    q: "What happens during the Revenue Opportunity Assessment?",
    a: "Qualified businesses receive access to the Apex Flow Assessment Portal to share goals, revenue data, lead flow, customer base, capacity, and current systems. This lets us identify and prioritize the highest-value opportunities.",
  },
  {
    q: "Is the Revenue Opportunity Roadmap required?",
    a: "It is the recommended starting point for most businesses because it provides a clear, human-reviewed view of where revenue is leaking and what to fix first. If you enroll in a monthly package within 30 days, your $750 Roadmap investment is credited toward your first month.",
  },
  {
    q: "What happens after I enroll in a monthly package?",
    a: "Apex Flow helps install the right systems, campaigns, follow-up, visibility, reporting, and strategic support—then manages execution and optimization behind the scenes while you stay focused on running your business.",
  },
  {
    q: "Can I see what Apex Flow is working on?",
    a: "Yes. The Apex Flow Client Portal gives you a clear view of progress, priorities, approvals, and next actions, while Apex Flow handles the execution and optimization for you.",
  },
  {
    q: "Is there a minimum commitment?",
    a: "Yes. Every Apex Flow package includes a three-month minimum commitment so the systems have time to be installed, optimized, and produce meaningful, measurable results.",
  },
  {
    q: "Do you guarantee results?",
    a: "No ethical growth partner can guarantee revenue outcomes. Results depend on market, lead volume, pricing, service quality, sales process, capacity, customer demand, and follow-through. Apex Flow focuses on identifying and improving the highest-value opportunities responsibly.",
  },
];

function Item({ q, a, open, onClick, idx }) {
  return (
    <div className="border-b border-brand-gray-soft">
      <button
        data-testid={`faq-trigger-${idx}`}
        onClick={onClick}
        className="group flex w-full items-center justify-between gap-6 py-6 text-left"
      >
        <span className="font-sans text-lg md:text-xl font-medium tracking-tight text-brand-black group-hover:text-brand-gold-deep transition-colors">
          {q}
        </span>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${open ? "bg-brand-black border-brand-black" : "border-brand-gray-soft"}`}>
          <Plus className={`h-4 w-4 transition-all duration-300 ${open ? "rotate-45 text-white" : "text-brand-gold"}`} strokeWidth={2} />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-6 pr-12 font-sans text-[15px] leading-relaxed text-brand-black/65">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="faq-accordion" id="faq" className="relative bg-white py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center">
          <Reveal><Eyebrow className="justify-center">Questions, Answered</Eyebrow></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-brand-black">
              Frequently Asked <span className="gold-text">Questions</span>
            </h2>
          </Reveal>
        </div>

        <Reveal delay={1} className="mt-14">
          {faqs.map((f, i) => (
            <Item key={f.q} idx={i} {...f} open={open === i} onClick={() => setOpen(open === i ? -1 : i)} />
          ))}
        </Reveal>
      </div>
    </section>
  );
}
